import { motion } from 'framer-motion'
import clsx from 'clsx'
import { stageIcons, stageLabels } from './constants'

interface AnalyzingStepProps {
  stages: Array<{ stage: string; message: string; data?: any }>
  currentStage: string
}

export function AnalyzingStep({ stages, currentStage }: AnalyzingStepProps) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-signal-blue/20 flex items-center justify-center">
          <span className="text-3xl animate-pulse">🔮</span>
        </div>
        <h3 className="text-lg font-mono text-star">Analyzing Question</h3>
        <p className="text-sm text-star-muted mt-1">Discovering relevant facts and details...</p>
      </div>

      {/* Progress stages */}
      <div className="space-y-3">
        {stages.map((s, i) => {
          const isActive = s.stage === currentStage && currentStage !== 'complete'
          const isComplete =
            stages.findIndex((st) => st.stage === currentStage) > i || currentStage === 'complete'

          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
              className={clsx(
                'p-3 rounded-lg border transition-all',
                isActive && 'bg-signal-blue/10 border-signal-blue/30',
                isComplete && !isActive && 'bg-signal-green/5 border-signal-green/20',
                !isActive && !isComplete && 'bg-void-50 border-star-dim/10'
              )}
            >
              <div className="flex items-center gap-3">
                <span className={clsx('text-xl', isActive && 'animate-bounce')}>
                  {isComplete ? '✅' : stageIcons[s.stage] || '⏳'}
                </span>
                <div className="flex-1">
                  <div
                    className={clsx(
                      'text-sm font-medium',
                      isActive
                        ? 'text-signal-blue'
                        : isComplete
                          ? 'text-signal-green'
                          : 'text-star-muted'
                    )}
                  >
                    {stageLabels[s.stage] || s.stage}
                  </div>
                  <div className="text-xs text-star-dim">{s.message}</div>
                </div>
                {isActive && (
                  <span className="w-4 h-4 border-2 border-signal-blue/30 border-t-signal-blue rounded-full animate-spin" />
                )}
              </div>

              {/* Show discovered facts */}
              {s.data?.facts && (
                <div className="mt-2 pl-9 space-y-1">
                  {s.data.facts.slice(0, 2).map((fact: any, j: number) => (
                    <div key={j} className="text-xs text-star-muted p-2 bg-void rounded">
                      <div className="font-medium text-star">{fact.title}</div>
                      <div className="line-clamp-2">{fact.snippet}</div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}
